import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { FaShieldAlt, FaUserLock, FaFileAlt, FaCookie, FaEnvelope, FaExternalLinkAlt } from 'react-icons/fa';
import { SectionHeader, Card, AnimatedSection } from '../components/common';

const Privacy = () => {
  const sections = [
    {
      id: 'privacy',
      icon: FaUserLock,
      title: '1. Privacy Policy',
      subsections: [
        {
          title: '1.1 Information Collected',
          content: `This Site displays personal and professional information about the owner for career and networking purposes. Limited data is collected as follows:`,
          list: [
            'Information you provide voluntarily through contact forms or email (e.g., name, email).',
            'Technical data automatically collected for analytics and security, including IP address, browser type, device details, and pages visited.'
          ],
          note: 'No sensitive personal data is collected.'
        },
        {
          title: '1.2 Resume Download',
          content: 'The resume is provided solely for professional review. No personal data is collected during download.'
        },
        {
          title: '1.3 Use of Information',
          content: 'Collected data is used to:',
          list: [
            'Respond to inquiries',
            'Improve website performance and analytics',
            'Maintain security and prevent misuse'
          ],
          note: 'No data is sold, shared, or exchanged with third parties except essential service providers (hosting, email, analytics).'
        },
        {
          title: '1.4 Data Protection',
          content: 'Reasonable security measures are implemented to protect your information. However, internet transmission cannot be guaranteed as fully secure.'
        },
        {
          title: '1.5 Your Rights',
          content: 'You may request access, correction, or deletion of any personal data you have provided by contacting the owner at the email provided below.'
        }
      ]
    },
    {
      id: 'terms',
      icon: FaFileAlt,
      title: '2. Terms & Conditions',
      subsections: [
        {
          title: '2.1 Use of the Site',
          content: 'This Site is intended for viewing personal and professional information, portfolio content, and resume access. You agree not to:',
          list: [
            'Copy, misuse, or distribute content for unauthorized purposes',
            'Engage in activities that harm the Site, such as hacking or spamming',
            'Use the Site in violation of applicable laws'
          ]
        },
        {
          title: '2.2 Intellectual Property',
          content: 'All content on the Site—including text, images, resume, design, and branding—belongs to the site owner unless stated otherwise.',
          note: 'Reproduction without permission is prohibited.'
        },
        {
          title: '2.3 Availability',
          content: 'The owner reserves the right to update, modify, or remove content and suspend or discontinue sections of the Site without notice.'
        }
      ]
    },
    {
      id: 'disclaimer',
      icon: FaShieldAlt,
      title: '3. Disclaimer',
      subsections: [
        {
          title: '3.1 General Information Only',
          content: 'All content on this Site, including professional details, biography, and resume, is provided for informational purposes. The owner does not guarantee:',
          list: [
            'Accuracy, completeness, or timeliness of information',
            'Suitability of content for any purpose'
          ],
          note: 'Use of any information from this Site is at your own risk.'
        },
        {
          title: '3.2 External Links',
          content: 'The Site may contain links to third-party websites. The owner is not responsible for external content, privacy practices, or accuracy of information found on third-party sites.'
        }
      ]
    },
    {
      id: 'cookies',
      icon: FaCookie,
      title: '4. Cookie Notice',
      subsections: [
        {
          title: '4.1 Use of Cookies',
          content: 'This Site may use basic cookies or analytics tools (e.g., Google Analytics) to improve performance and measure traffic.'
        },
        {
          title: '4.2 Cookie Control',
          content: 'You may disable cookies through your browser settings. Some features may not work optimally without cookies.'
        }
      ]
    }
  ];

  return (
    <>
      <Helmet>
        <title>Privacy Policy & Legal Notice | Rakesh Kumar Tripathi</title>
        <meta name="description" content="Privacy Policy, Terms & Conditions, Disclaimer, and Cookie Notice for Rakesh Kumar Tripathi's personal website." />
      </Helmet>

      {/* Hero Section */}
      <section className="pt-8 pb-8 gradient-bg-subtle">
        <div className="container-custom">
          <AnimatedSection>
            <SectionHeader
              badge="Legal"
              title="Website Legal Notice"
              subtitle="Privacy Policy, Terms & Conditions, Disclaimer, and Cookie Notice"
            />
            <p className="text-center text-dark-500 -mt-8">
              <strong>Last Updated:</strong> 13 Dec 2025
            </p>
          </AnimatedSection>
        </div>
      </section>

      {/* Quick Navigation */}
      <section className="py-6 bg-white border-b">
        <div className="container-custom">
          <div className="flex flex-wrap justify-center gap-3">
            {sections.map((section) => (
              <a
                key={section.id}
                href={`#${section.id}`}
                className="flex items-center space-x-2 px-4 py-2 bg-primary-50 rounded-full text-sm font-medium text-primary-600 hover:bg-primary-100 transition-colors"
              >
                <section.icon className="w-4 h-4" />
                <span>{section.title.split(' ').slice(1).join(' ')}</span>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Content Sections */}
      <section className="section-padding bg-gray-50">
        <div className="container-custom max-w-4xl">
          <div className="space-y-8">
            {sections.map((section, sectionIndex) => (
              <motion.div
                key={section.id}
                id={section.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: sectionIndex * 0.1 }}
              >
                <Card elevated padding="lg">
                  <div className="flex items-center space-x-3 mb-6">
                    <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center">
                      <section.icon className="w-6 h-6 text-primary-500" />
                    </div>
                    <h2 className="text-2xl font-bold text-dark-800">{section.title}</h2>
                  </div>

                  <div className="space-y-6">
                    {section.subsections.map((sub, subIndex) => (
                      <div key={subIndex} className="border-l-2 border-primary-200 pl-4">
                        <h3 className="text-lg font-semibold text-dark-700 mb-2">{sub.title}</h3>
                        <p className="text-dark-600 mb-3">{sub.content}</p>

                        {sub.list && (
                          <ul className="list-disc list-inside space-y-1 mb-3 text-dark-600">
                            {sub.list.map((item, i) => (
                              <li key={i} className="pl-2">{item}</li>
                            ))}
                          </ul>
                        )}

                        {sub.note && (
                          <p className="text-sm text-dark-500 italic bg-gray-50 p-3 rounded-lg">
                            {sub.note}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </Card>
              </motion.div>
            ))}

            {/* Contact Information */}
            <motion.div
              id="contact"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <Card elevated padding="lg">
                <div className="flex items-center space-x-3 mb-6">
                  <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center">
                    <FaEnvelope className="w-6 h-6 text-primary-500" />
                  </div>
                  <h2 className="text-2xl font-bold text-dark-800">5. Contact Information</h2>
                </div>

                <p className="text-dark-600 mb-4">
                  For questions, requests, or concerns regarding this legal notice, please contact:
                </p>

                <div className="bg-gradient-to-br from-primary-50 to-secondary-50 rounded-xl p-6">
                  <p className="font-semibold text-dark-800 text-lg mb-2">Rakesh Kumar Tripathi</p>
                  <p className="text-dark-600 mb-1">
                    <strong>Email:</strong>{' '}
                    <a href="mailto:technovb@gmail.com" className="text-primary-500 hover:underline">
                      technovb@gmail.com
                    </a>
                  </p>
                  <p className="text-dark-600">
                    <strong>Location:</strong> Meerut, India
                  </p>
                </div>
              </Card>
            </motion.div>

            {/* Updates Notice */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <Card elevated padding="lg">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center">
                    <FaExternalLinkAlt className="w-5 h-5 text-primary-500" />
                  </div>
                  <h2 className="text-2xl font-bold text-dark-800">6. Updates to This Legal Section</h2>
                </div>

                <p className="text-dark-600">
                  This combined policy may be updated periodically. The updated version will be posted on this page with a new "Last Updated" date.
                </p>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Privacy;
